import { FSA } from '../../../shared/types'
import {
  AAA_ASSIST,
  AAA_ASSIST_CONFIRM,
  AAA_ASSIST_RESET,
  AAA_ASSIST_SEND_TURN,
  AAA_ASSIST_START,
} from './aaa-assist.actions'
import {
  AaaAssistClarifyTurn,
  AaaAssistConfirmResponse,
  AaaAssistError,
  ScenarioMessage,
  ScenarioResponse,
} from './aaa-assist.types'

export interface AaaAssistState {
  sessionId: string | null
  messages: ScenarioMessage[]
  clarifyTurns: AaaAssistClarifyTurn[]
  latestResponse: ScenarioResponse | null
  confirmedResult: AaaAssistConfirmResponse | null
  inFlight: boolean
  error: AaaAssistError | null
}

export const initialState: AaaAssistState = {
  sessionId: null,
  messages: [],
  clarifyTurns: [],
  latestResponse: null,
  confirmedResult: null,
  inFlight: false,
  error: null,
}

const withLastAnswer = (
  turns: AaaAssistClarifyTurn[],
  answer: string | null,
  answerOptionCode: string | null
) =>
  turns.map((turn, index) =>
    index === turns.length - 1 ? { ...turn, answer, answerOptionCode } : turn
  )

export function reducer(
  state: AaaAssistState | undefined = initialState,
  action: FSA
): AaaAssistState {
  switch (action.type) {
    case AAA_ASSIST_START:
      return {
        ...initialState,
        sessionId: action.payload.sessionId,
        messages: [{ role: 'user', content: action.payload.scenario }],
        inFlight: true,
      }
    // Only an answer to a clarifying question continues the conversation: the question the
    // member just answered joins the transcript, followed by the answer itself. A turn sent while
    // a request is still running is dropped, so the pair can't be appended twice.
    case AAA_ASSIST_SEND_TURN: {
      if (state.inFlight || state.latestResponse?.status !== 'clarify') {
        return state
      }
      return {
        ...state,
        messages: [
          ...state.messages,
          { role: 'assistant', content: state.latestResponse.question },
          { role: 'user', content: action.payload.answer },
        ],
        clarifyTurns: withLastAnswer(state.clarifyTurns, action.payload.answer, action.payload.optionCode),
        inFlight: true,
        error: null,
      }
    }
    case AAA_ASSIST.SUCCESS:
      return {
        ...state,
        latestResponse: action.payload.response,
        clarifyTurns:
          action.payload.response.status === 'clarify'
            ? [
              ...state.clarifyTurns,
              {
                question: action.payload.response.question,
                candidates: action.payload.response.candidates,
                answer: null,
                answerOptionCode: null,
              },
            ]
            : state.clarifyTurns,
        inFlight: false,
        error: null,
      }
    // A later turn that failed never got an answer: drop its question/answer pair and clear the
    // turn's answer so the transcript only holds turns the backend saw. A failed first turn keeps
    // its scenario, and a failure after a classified response (an interrupted confirm) keeps the
    // transcript as is.
    case AAA_ASSIST.FAILURE:
      return {
        ...state,
        ...(state.inFlight && state.latestResponse?.status === 'clarify'
          ? {
            messages: state.messages.slice(0, -2),
            clarifyTurns: withLastAnswer(state.clarifyTurns, null, null),
          }
          : {}),
        error: action.payload.error,
        inFlight: false,
      }
    case AAA_ASSIST_CONFIRM.FAILURE:
      return {
        ...state,
        error: action.payload.error,
        inFlight: false,
      }
    // Only a classified response can be confirmed, once: a confirm sent while any request is still
    // running is dropped, so a double tap can't send it twice.
    case AAA_ASSIST_CONFIRM.REQUEST:
      if (state.inFlight || state.latestResponse?.status !== 'classified') {
        return state
      }
      return {
        ...state,
        inFlight: true,
        error: null,
      }
    case AAA_ASSIST_CONFIRM.SUCCESS:
      return {
        ...state,
        confirmedResult: action.payload.result,
        inFlight: false,
      }
    case AAA_ASSIST_RESET:
      return initialState
    default:
      return state
  }
}
